import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Plus, MapPin, Edit, Trash2, ChevronRight, Layers, DollarSign, Upload, Image, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import CrearLotesMasivo from '@/components/CrearLotesMasivo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Fraccionamientos() {
  const [fraccionamientos, setFraccionamientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFracc, setEditingFracc] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [planoModal, setPlanoModal] = useState({ open: false, url: null, nombre: '' });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingPlano, setUploadingPlano] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    ubicacion: '',
    tipo: 'publico',
    descripcion: '',
    precio_m2: '',
    moneda_precio_m2: 'USD'
  });
  
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [selectedFraccForBulk, setSelectedFraccForBulk] = useState(null);
  
  const logoInputRef = useRef(null);
  const planoInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFraccionamientos();
  }, []);

  const fetchFraccionamientos = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/fraccionamientos`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Error al cargar fraccionamientos');
      const data = await response.json();
      setFraccionamientos(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Error al cargar fraccionamientos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingFracc 
        ? `${BACKEND_URL}/api/fraccionamientos/${editingFracc.fraccionamiento_id}`
        : `${BACKEND_URL}/api/fraccionamientos`;
      
      const dataToSend = { ...formData };
      if (dataToSend.precio_m2) {
        dataToSend.precio_m2 = parseFloat(dataToSend.precio_m2);
      } else {
        delete dataToSend.precio_m2;
      }
      
      const response = await fetch(url, {
        method: editingFracc ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(dataToSend)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al guardar');
      }
      
      toast.success(editingFracc ? 'Fraccionamiento actualizado' : 'Fraccionamiento creado');
      handleCloseDialog();
      fetchFraccionamientos();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (fracc) => {
    setEditingFracc(fracc);
    setFormData({
      nombre: fracc.nombre,
      ubicacion: fracc.ubicacion,
      tipo: fracc.tipo,
      descripcion: fracc.descripcion || '',
      precio_m2: fracc.precio_m2?.toString() || '',
      moneda_precio_m2: fracc.moneda_precio_m2 || 'USD'
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/fraccionamientos/${deleteDialog.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al eliminar');
      }
      
      toast.success('Fraccionamiento eliminado');
      setDeleteDialog({ open: false, id: null });
      fetchFraccionamientos();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingFracc(null);
    setFormData({
      nombre: '',
      ubicacion: '',
      tipo: 'publico',
      descripcion: '',
      precio_m2: '',
      moneda_precio_m2: 'USD'
    });
  };

  const openBulkDialog = (fracc) => {
    setSelectedFraccForBulk(fracc);
    setBulkDialogOpen(true);
  };

  // Upload Logo
  const handleLogoUpload = async (e, fraccId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('El logo no debe superar 2MB');
      return;
    }
    
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/fraccionamientos/${fraccId}/upload-logo`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al subir logo');
      }
      
      toast.success('Logo actualizado');
      fetchFraccionamientos();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  // Upload Plano
  const handlePlanoUpload = async (e, fraccId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('El plano no debe superar 5MB');
      return;
    }
    
    setUploadingPlano(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/fraccionamientos/${fraccId}/upload-plano`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al subir plano');
      }
      
      toast.success('Plano actualizado');
      fetchFraccionamientos();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploadingPlano(false);
    }
  };

  const formatPrecio = (precio, moneda) => {
    if (!precio) return null;
    const symbol = moneda === 'MXN' ? 'MXN $' : 'USD $';
    return `${symbol}${precio.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Fraccionamientos</h1>
          <p className="text-zinc-500 text-sm mt-1">Administra tus desarrollos inmobiliarios</p>
        </div>
        <Button 
          onClick={() => setDialogOpen(true)} 
          className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
          data-testid="btn-nuevo-fracc"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Fraccionamiento
        </Button>
      </div>

      {/* Lista de Fraccionamientos */}
      {fraccionamientos.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-16 text-center">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
          <h3 className="text-lg font-bold text-white mb-2">No hay fraccionamientos</h3>
          <p className="text-zinc-500 mb-4">Crea tu primer fraccionamiento para comenzar</p>
          <Button onClick={() => setDialogOpen(true)} className="bg-amber-500 hover:bg-amber-400 text-black font-bold">
            <Plus className="w-4 h-4 mr-2" />
            Crear Fraccionamiento
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {fraccionamientos.map((fracc) => (
            <div
              key={fracc.fraccionamiento_id}
              data-testid={`fraccionamiento-card-${fracc.fraccionamiento_id}`}
              className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden hover:border-zinc-700 transition-all"
            >
              {/* Área superior con Logo */}
              <div className="p-5 pb-4">
                <div className="flex items-start gap-4">
                  {/* Logo */}
                  <div 
                    className="w-16 h-16 rounded-xl bg-zinc-800 border border-zinc-700 flex items-center justify-center overflow-hidden flex-shrink-0 cursor-pointer group relative"
                    onClick={() => document.getElementById(`logo-input-${fracc.fraccionamiento_id}`).click()}
                  >
                    {fracc.logo_url ? (
                      <img 
                        src={`${BACKEND_URL}${fracc.logo_url}`} 
                        alt={fracc.nombre}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Building2 className="w-8 h-8 text-zinc-600" />
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Upload className="w-5 h-5 text-white" />
                    </div>
                    <input
                      id={`logo-input-${fracc.fraccionamiento_id}`}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => handleLogoUpload(e, fracc.fraccionamiento_id)}
                    />
                  </div>
                  
                  {/* Info principal */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                        fracc.tipo === 'privado'
                          ? 'bg-orange-500/20 text-orange-400'
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {fracc.tipo === 'privado' ? 'Privado' : 'Público'}
                      </span>
                      
                      {/* Icono Ver Plano */}
                      {fracc.plano_url && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPlanoModal({ open: true, url: `${BACKEND_URL}${fracc.plano_url}`, nombre: fracc.nombre });
                          }}
                          className="p-1 rounded hover:bg-zinc-700 transition-colors"
                          title="Ver plano"
                        >
                          <FileText className="w-4 h-4 text-zinc-400" />
                        </button>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-white truncate">{fracc.nombre}</h3>
                    <div className="flex items-center gap-1.5 text-sm text-zinc-500 mt-1">
                      <MapPin className="w-3.5 h-3.5" />
                      <span className="truncate">{fracc.ubicacion}</span>
                    </div>
                  </div>
                </div>
                
                {/* Precio por m² */}
                {fracc.precio_m2 && (
                  <div className="mt-4 p-3 bg-[#C9A24D]/10 rounded-lg border border-[#C9A24D]/20">
                    <p className="text-xs text-[#D4AF37]/70 mb-0.5">Precio por m²</p>
                    <p className="text-xl font-bold text-[#D4AF37] font-mono">
                      {formatPrecio(fracc.precio_m2, fracc.moneda_precio_m2)}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Acciones */}
              <div className="px-5 pb-5 pt-2 border-t border-zinc-800/50">
                <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    {/* Subir Plano */}
                    <button
                      onClick={() => document.getElementById(`plano-input-${fracc.fraccionamiento_id}`).click()}
                      className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
                      title="Subir plano"
                    >
                      <Image className="w-4 h-4" />
                    </button>
                    <input
                      id={`plano-input-${fracc.fraccionamiento_id}`}
                      type="file"
                      accept="image/png,image/jpeg,application/pdf"
                      className="hidden"
                      onChange={(e) => handlePlanoUpload(e, fracc.fraccionamiento_id)}
                    />
                    
                    {/* Crear Lotes Masivo */}
                    <button
                      onClick={() => openBulkDialog(fracc)}
                      className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-[#D4AF37] transition-colors"
                      title="Crear lotes masivamente"
                    >
                      <Layers className="w-4 h-4" />
                    </button>
                    
                    {/* Editar */}
                    <button
                      onClick={() => handleEdit(fracc)}
                      className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-amber-400 transition-colors"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    
                    {/* Eliminar */}
                    <button
                      onClick={() => setDeleteDialog({ open: true, id: fracc.fraccionamiento_id })}
                      className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-red-400 transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Ver Detalles */}
                  <Button
                    onClick={() => navigate(`/fraccionamientos/${fracc.fraccionamiento_id}`)}
                    className="bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border-0"
                    size="sm"
                  >
                    Ver detalles
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Crear/Editar */}
      <Dialog open={dialogOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
        <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingFracc ? 'Editar Fraccionamiento' : 'Nuevo Fraccionamiento'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-zinc-400">Nombre</Label>
              <Input
                data-testid="input-nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                required
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="Ej: Residencial Las Palmas"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Ubicación</Label>
              <Input
                data-testid="input-ubicacion"
                value={formData.ubicacion}
                onChange={(e) => setFormData({...formData, ubicacion: e.target.value})}
                required
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="Ej: Culiacán, Sinaloa"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Tipo</Label>
              <Select value={formData.tipo} onValueChange={(value) => setFormData({...formData, tipo: value})}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="publico" className="text-white hover:bg-zinc-800">Público</SelectItem>
                  <SelectItem value="privado" className="text-white hover:bg-zinc-800">Privado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Precio por m² */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400">Precio por m²</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.precio_m2}
                  onChange={(e) => setFormData({...formData, precio_m2: e.target.value})}
                  placeholder="Ej: 850"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Moneda</Label>
                <Select value={formData.moneda_precio_m2} onValueChange={(value) => setFormData({...formData, moneda_precio_m2: value})}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="USD" className="text-white hover:bg-zinc-800">USD</SelectItem>
                    <SelectItem value="MXN" className="text-white hover:bg-zinc-800">MXN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-400">Descripción (opcional)</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                rows={2}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="Breve descripción del fraccionamiento..."
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCloseDialog} 
                className="flex-1 border-zinc-700 text-white hover:bg-zinc-800"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold"
              >
                {editingFracc ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal Ver Plano */}
      <Dialog open={planoModal.open} onOpenChange={(open) => !open && setPlanoModal({ open: false, url: null, nombre: '' })}>
        <DialogContent className="sm:max-w-3xl bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-500" />
              Plano - {planoModal.nombre}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            {planoModal.url && (
              planoModal.url.endsWith('.pdf') ? (
                <iframe 
                  src={planoModal.url} 
                  className="w-full h-[60vh] rounded-lg border border-zinc-800"
                  title="Plano del fraccionamiento"
                />
              ) : (
                <img 
                  src={planoModal.url} 
                  alt={`Plano de ${planoModal.nombre}`}
                  className="w-full max-h-[60vh] object-contain rounded-lg"
                />
              )
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Eliminar */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, id: null })}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Eliminar fraccionamiento?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Esta acción no se puede deshacer. Se eliminará permanentemente el fraccionamiento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 text-white hover:bg-zinc-800">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Crear Lotes Masivo */}
      {bulkDialogOpen && selectedFraccForBulk && (
        <CrearLotesMasivo
          open={bulkDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              setBulkDialogOpen(false);
              setSelectedFraccForBulk(null);
            }
          }}
          fraccionamiento={selectedFraccForBulk}
          onSuccess={() => {
            setBulkDialogOpen(false);
            setSelectedFraccForBulk(null);
            toast.success('Lotes creados exitosamente');
          }}
        />
      )}
    </div>
  );
}
